import { ArrowLeft, AtSign, Send } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PLATFORM_LABELS, type BrandMention } from '@/types/brandMentions'
import {
	formatMentionDate,
	highlightBrand,
} from '@/components/brand-mentions/mentionUtils'

export function MentionDetail({
	mention,
	brandName,
	locale,
	onBack,
}: {
	mention: BrandMention | null
	brandName: string | null
	locale: string
	onBack?: () => void
}) {
	const { t } = useTranslation()

	if (!mention) {
		return (
			<div className="flex h-full flex-col items-center justify-center p-8 text-center">
				<AtSign className="mb-4 h-16 w-16 text-muted-foreground/50" />
				<p className="text-sm text-muted-foreground">
					{t('brandMentions.selectMention')}
				</p>
			</div>
		)
	}

	const platformLabel = PLATFORM_LABELS[mention.platform]

	return (
		<div className="flex h-full flex-col overflow-hidden bg-background">
			<header className="flex-shrink-0 border-b bg-background/95 px-3 py-3 sm:px-4">
				<div className="flex items-center gap-3">
					{onBack && (
						<Button
							variant="ghost"
							size="icon"
							className="h-9 w-9 shrink-0"
							onClick={onBack}
							aria-label={t('brandMentions.backToList')}>
							<ArrowLeft className="h-4 w-4" />
						</Button>
					)}
					<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
						<PlatformIcon platform={mention.platform} className="h-5 w-5" colored />
					</div>
					<div className="min-w-0 flex-1">
						<div className="flex items-center gap-2">
							<h2 className="truncate text-base font-semibold">
								{mention.author || t('brandMentions.authorUnknown')}
							</h2>
							{mention.kind === 'Comment' && (
								<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
									{t('brandMentions.commentKind')}
								</span>
							)}
						</div>
						<div className="flex items-center gap-2 text-xs text-muted-foreground">
							<span>{platformLabel}</span>
							<span>•</span>
							<time dateTime={mention.publishedAt}>
								{formatMentionDate(mention.publishedAt, locale)}
							</time>
						</div>
					</div>
				</div>
			</header>
			<div className="flex-1 overflow-y-auto p-4 sm:p-6">
				<article className="mx-auto max-w-3xl">
					{mention.title && (
						<h3 className="mb-3 text-lg font-semibold">
							{highlightBrand(mention.title, brandName)}
						</h3>
					)}
					<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
						{highlightBrand(mention.text, brandName)}
					</p>
				</article>
			</div>
			<footer className="flex-shrink-0 border-t bg-background/95 p-3 sm:p-4">
				<a
					href={mention.url}
					target="_blank"
					rel="noopener noreferrer"
					className={cn(
						'flex items-center gap-3 rounded-xl border-2 border-border/40 bg-muted/30 px-4 py-3',
						'transition-all duration-200 hover:border-primary/50 hover:bg-background',
						'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/10'
					)}>
					<span className="min-w-0 flex-1 text-sm text-muted-foreground">
						{t('brandMentions.replyPlaceholder', { platform: platformLabel })}
					</span>
					<Button size="icon" className="h-9 w-9 shrink-0 shadow-md" asChild>
						<span>
							<Send className="h-4 w-4" />
							<span className="sr-only">
								{t('brandMentions.replyOnPlatform', { platform: platformLabel })}
							</span>
						</span>
					</Button>
				</a>
				<p className="mt-2 px-1 text-xs text-muted-foreground/60">
					{t('brandMentions.replyHint')}
				</p>
			</footer>
		</div>
	)
}
